import * as THREE from 'three'
import type { FrameDriver } from '@/graphics/FrameDriver'
import type { GraphicsBackend } from '@/graphics/GraphicsBackend'
import { resolveGraphicsConfig } from '@/graphics/GraphicsConfig'
import { WebGPUDeviceLostError } from '@/graphics/GraphicsErrors'
import { collectWebGPUDeviceRequirements } from '@/graphics/GraphicsRequirements'
import { WebGL2Backend } from '@/graphics/WebGL2Backend'
import { WebGLFrameDriver } from '@/graphics/WebGLFrameDriver'
import { EngineContext } from '@/runtime/EngineContext'
import { EventBus } from '@/runtime/EventBus'
import { PluginManager } from '@/runtime/PluginManager'
import { RenderLoop } from '@/runtime/RenderLoop'
import { SceneBoundsService } from '@/runtime/SceneBoundsService'
import { SceneGraphService } from '@/runtime/SceneGraphService'
import { DisposableScope } from '@/shared/disposable/DisposableScope'
import { createLoggerManager, LogLevel } from '@/shared/logging'
import type { ViewportCamera } from '@/shared/types/services'
import { PotreeEngine } from './PotreeEngine'
import type { EngineConfig } from './types'

/** 构建期注入的生产标记，未定义时按非生产处理（如 example 直接消费 src） */
declare const __SHARE3D_PROD__: boolean | undefined

/** 解析环境默认日志级别：生产 SILENT，开发 WARN */
function resolveDefaultLogLevel(): LogLevel {
  const isProd = typeof __SHARE3D_PROD__ !== 'undefined' && __SHARE3D_PROD__ === true
  return isProd ? LogLevel.SILENT : LogLevel.WARN
}

/**
 * 引擎工厂函数，创建并完成初始化的 PotreeEngine。
 *
 * 依次完成渲染器创建、基础服务装配、插件注册与初始化、渲染循环启动。
 * 任何步骤失败时按创建逆序释放已分配资源。
 *
 * @param config - 引擎初始化配置
 * @returns 就绪的 PotreeEngine 实例
 */
export async function createPotreeEngine(config: EngineConfig): Promise<PotreeEngine> {
  let renderer: THREE.WebGLRenderer | null = null
  let graphics: GraphicsBackend | null = null
  let frameDriver: FrameDriver | null = null
  let events: EventBus | null = null
  let context: EngineContext | null = null
  let pluginManager: PluginManager | null = null
  let loop: RenderLoop | null = null
  try {
    // ── 准备阶段：创建 EngineContext 所需的基础设施 ──

    const graphicsConfig = resolveGraphicsConfig(config.graphics, config.renderer)
    const webgpuDeviceRequirements =
      graphicsConfig.backend === 'webgpu'
        ? collectWebGPUDeviceRequirements(config.plugins)
        : undefined
    // 校验容器尺寸
    const width = config.container.clientWidth
    const height = config.container.clientHeight
    if (width === 0 || height === 0) {
      throw new Error('container 尺寸为 0，请确保 container 已添加到 DOM 且可见')
    }

    // 优先初始化 LoggerManager，使后续基础设施（含 EventBus）即可记录日志
    const loggerManager = createLoggerManager(config.logging, resolveDefaultLogLevel())
    const logger = loggerManager.root

    // 创建 EventBus，使 backend 初始化后的异步错误已有统一出口。
    events = new EventBus()

    const reportGraphicsError = (error: Error, phase: string): void => {
      if (events) {
        events.emit('engine.error', { error, context: `graphics:${phase}` })
        return
      }
      logger.error(`event=graphics.init.cleanup.failed phase=${phase} errorName=${error.name}`)
    }
    const renderMode = config.renderMode ?? 'continuous'

    if (graphicsConfig.backend === 'webgl2') {
      renderer = new THREE.WebGLRenderer({
        // 高斯 splat 是极重的过度绘制负载：默认关闭 MSAA 与 WebGPU 路径和 PlayCanvas 保持一致，
        // 需要 MSAA 的宿主可显式传 renderer.antialias = true。
        antialias: config.renderer?.antialias ?? false,
        preserveDrawingBuffer: config.renderer?.preserveDrawingBuffer ?? false,
        logarithmicDepthBuffer: config.renderer?.logarithmicDepthBuffer ?? false,
        powerPreference: (config.renderer?.powerPreference ??
          'high-performance') as WebGLPowerPreference,
        canvas: document.createElement('canvas'),
      })
      graphics = new WebGL2Backend({
        renderer,
        container: config.container,
        pixelRatio: config.pixelRatio,
        maxPixelRatio: graphicsConfig.maxPixelRatio,
        onError: reportGraphicsError,
      })
      frameDriver = new WebGLFrameDriver({ mode: renderMode, onError: reportGraphicsError })
    } else {
      const { WebGPUBackend } = await import('@/graphics/WebGPUBackend')
      const webgpuBackend = await WebGPUBackend.create({
        container: config.container,
        pixelRatio: config.pixelRatio,
        maxPixelRatio: graphicsConfig.maxPixelRatio,
        powerPreference: graphicsConfig.powerPreference,
        gpuProfiling: graphicsConfig.gpuProfiling,
        deviceRequirements: webgpuDeviceRequirements,
        onError: reportGraphicsError,
        onDeviceLost: (details) => {
          frameDriver?.stop()
          events?.emit('engine.error', {
            error: new WebGPUDeviceLostError(details.message, details.reason),
            context: 'graphics:webgpu.deviceLost',
          })
        },
      })
      graphics = webgpuBackend
      frameDriver = webgpuBackend.createFrameDriver(renderMode, reportGraphicsError)
      graphics.assertCanSubmit()
    }

    // 创建 SceneGraphService
    const sceneGraph = new SceneGraphService()
    const sceneBounds = new SceneBoundsService()
    if (config.background != null) {
      const bgColor =
        typeof config.background === 'string'
          ? new THREE.Color(config.background)
          : config.background
      sceneGraph.setBackground(bgColor)
    }

    // 创建 DisposableScope
    const disposables = new DisposableScope()

    // 创建 LoadingManager 并桥接到 EventBus
    const loadingManager = new THREE.LoadingManager()
    loadingManager.onLoad = () => {
      events!.emit('asset.allLoaded', undefined)
    }
    loadingManager.onProgress = (_url, loaded, total) => {
      events!.emit('asset.progress', {
        url: _url,
        loaded,
        total,
        progress: total > 0 ? loaded / total : 0,
      })
    }
    loadingManager.onError = (url) => {
      events!.emit('engine.error', {
        error: new Error(`加载失败: ${url}`),
        context: 'loadingManager',
      })
    }

    // ── 10 步初始化序列 ──

    // 步骤 1: 创建 EngineContext
    context = new EngineContext({
      container: config.container,
      graphics,
      renderer: renderer ?? undefined,
      events,
      sceneGraph,
      sceneBounds,
      disposables,
      loadingManager,
      logger,
      pointBudget: config.pointBudget,
      maxNodesLoading: config.maxNodesLoading,
      unitPreference: config.unitPreference,
    })

    // 步骤 2: 创建 PluginManager
    pluginManager = new PluginManager()

    // 步骤 3: 注册所有 plugins
    pluginManager.addAll(config.plugins)

    // 步骤 4: 初始化 plugins（preload → onInit → onStart）
    await pluginManager.init(context)

    // 步骤 5: 从 context 读取 ViewportCamera
    let viewportCamera: ViewportCamera | null = null
    if (context.hasService('ViewportCamera')) {
      viewportCamera = context.getService('ViewportCamera') as ViewportCamera
    }

    // 步骤 6: 创建 RenderLoop
    loop = new RenderLoop(
      context,
      pluginManager,
      viewportCamera,
      config.pixelRatio,
      renderMode,
      frameDriver
    )

    // 步骤 7: 构造 PotreeEngine（含步骤 8 创建 ActivationToken、步骤 9 注入 token 到 ToolPlugin）
    const engine = new PotreeEngine({
      context,
      plugins: pluginManager,
      events,
      loop,
      pluginManager,
    })

    // 步骤 10: 启动 loop，广播 engine ready
    if (config.statsContainer) {
      loop.enableStats(config.statsContainer)
    }
    loop.start()
    engine.markReady()
    events.emit('engine.ready', undefined)

    return engine
  } catch (error) {
    // 初始化失败时按创建逆序释放已创建资源（每步单独 try-catch 防止清理中断）
    try {
      if (loop) {
        loop.dispose()
      } else {
        frameDriver?.dispose()
      }
    } catch {
      /* ignore */
    }
    try {
      if (pluginManager) pluginManager.destroy()
    } catch {
      /* ignore */
    }
    try {
      if (context) context.dispose()
    } catch {
      /* ignore */
    }
    try {
      if (graphics) {
        graphics.dispose()
      } else if (renderer) {
        renderer.dispose()
        renderer.domElement.remove()
      }
    } catch {
      /* ignore */
    }
    try {
      if (events) events.dispose()
    } catch {
      /* ignore */
    }
    try {
      pluginManager?.releaseReferences()
      context?.releaseHostReferences()
    } catch {
      /* ignore */
    }
    throw error
  }
}
